/* ==========================================================================
   Dalen — Areviews integration.
   Pulls real review data from the Areviews app via its Shopify App Proxy
   (POST /apps/aliexpress_reviews, same-origin on the store domain) and
   renders it into our own custom reviews design (.dalen-reviews-summary +
   .dalen-review-card list). The app's own widget/styling is NOT used — we
   only consume its data.

   The proxy returns a pre-rendered HTML fragment (the #az_reviews block).
   We parse the values we need out of that fragment with DOMParser
   (scripts inside a DOMParser document never execute, so this is safe)
   and rebuild the markup in our design.

   Graceful fallback: the static review blocks rendered by Liquid stay in
   place and are only replaced AFTER a successful fetch+parse. If the proxy
   is unreachable (e.g. a bare localhost outside the Shopify proxy), the
   static content remains.
   ========================================================================== */
(function () {
  var PROXY_URL = '/apps/aliexpress_reviews';
  var PER_PAGE = 10; // Areviews returns 10 reviews per page

  /* Map our sort dropdown values → Areviews sort_value tokens. */
  var SORT_MAP = {
    recent: 'date',
    highest: 'stars',
    lowest: 'stars_old'
  };

  function init() {
    var roots = document.querySelectorAll('[data-reviews-list][data-reviews-source="areviews"]');
    roots.forEach(setup);
    // Only wire the submission form to Areviews when at least one list on the
    // page is in Areviews mode.
    if (roots.length) wireReviewForms();
  }

  /* ----------------------------------------------------------------------
     Submit "Write a Review" straight to the Areviews app instead of the
     Shopify contact form. Endpoint + field names come from the app's own
     submit handler: POST /apps/aliexpress_reviews/store_reviews with
     comment_name, email, comment_rating_ra, review_comment, product_id,
     customer_country (+ optional images[]).
     ---------------------------------------------------------------------- */
  function wireReviewForms() {
    document.querySelectorAll('[data-review-form]').forEach(function (form) {
      if (form.dataset.areviewsSubmit) return;
      form.dataset.areviewsSubmit = 'true';

      var section = form.closest('.dalen-review-modal');
      var sectionData = document.querySelector('[data-areviews-section]');
      var shop = sectionData ? sectionData.getAttribute('data-shop') : '';

      // Image upload — show thumbnail previews and cap at 5 files / 5MB each.
      var imageInput = form.querySelector('[data-review-images]');
      var previewWrap = form.querySelector('[data-review-image-previews]');
      var MAX_IMAGES = 5;
      var MAX_BYTES = 5 * 1024 * 1024;
      if (imageInput) {
        imageInput.addEventListener('change', function () {
          if (previewWrap) previewWrap.innerHTML = '';
          var files = Array.prototype.slice.call(imageInput.files).slice(0, MAX_IMAGES);
          files.forEach(function (file) {
            if (!/^image\//.test(file.type) || file.size > MAX_BYTES) return;
            if (!previewWrap) return;
            var img = document.createElement('img');
            img.src = URL.createObjectURL(file);
            img.alt = file.name;
            previewWrap.appendChild(img);
          });
        });
      }

      form.addEventListener('submit', function (e) {
        // Take over from the Shopify contact-form action.
        e.preventDefault();

        var nameEl = form.querySelector('[name="contact[name]"]');
        var emailEl = form.querySelector('[name="contact[email]"]');
        var titleEl = form.querySelector('[name="contact[Review Title]"]');
        var bodyEl = form.querySelector('[name="contact[body]"]');
        var ratingEl = form.querySelector('[data-rating-value]');
        var productEl = form.querySelector('[name="contact[Product ID]"]');
        var submitBtn = form.querySelector('.dalen-review-form__submit');

        var rating = ratingEl ? parseInt(ratingEl.value, 10) : 0;
        if (!rating) {
          showFormMessage(form, 'Please select a star rating.', false);
          return;
        }
        var productId = productEl ? productEl.value : '';
        if (!productId) {
          // Without a product id we can't attach the review; let the native
          // contact form take over as a fallback.
          form.submit();
          return;
        }

        var title = titleEl ? titleEl.value.trim() : '';
        var body = bodyEl ? bodyEl.value.trim() : '';
        var comment = title ? (title + ' — ' + body) : body;

        var fd = new FormData();
        fd.append('comment_name', nameEl ? nameEl.value.trim() : '');
        fd.append('email', emailEl ? emailEl.value.trim() : '');
        fd.append('comment_rating_ra', String(rating));
        fd.append('review_comment', comment);
        fd.append('product_id', String(productId));
        fd.append('customer_country', 'en');

        // Attach up to 5 valid image files as images[] (the field Areviews expects).
        if (imageInput && imageInput.files && imageInput.files.length) {
          var files = Array.prototype.slice.call(imageInput.files).slice(0, MAX_IMAGES);
          var tooBig = false;
          files.forEach(function (file) {
            if (!/^image\//.test(file.type)) return;
            if (file.size > MAX_BYTES) { tooBig = true; return; }
            fd.append('images[]', file);
          });
          if (tooBig) {
            showFormMessage(form, 'Some images were larger than 5MB and were skipped.', false);
          }
        }

        if (submitBtn) { submitBtn.disabled = true; submitBtn.dataset.label = submitBtn.textContent; submitBtn.textContent = 'Submitting…'; }

        fetch(PROXY_URL + '/store_reviews', { method: 'POST', body: fd })
          .then(function (res) {
            if (!res.ok) throw new Error('store_reviews ' + res.status);
            return res.text();
          })
          .then(function () {
            showFormMessage(form, 'Thanks! Your review has been submitted and will appear once approved.', true);
            form.reset();
            if (ratingEl) ratingEl.value = '';
            form.querySelectorAll('.dalen-review-form__rating-star.is-active').forEach(function (s) {
              s.classList.remove('is-active');
            });
            if (previewWrap) previewWrap.innerHTML = '';
          })
          .catch(function (err) {
            if (window.console && console.warn) console.warn('[Dalen Areviews] submit failed', err.message);
            showFormMessage(form, 'Sorry — something went wrong submitting your review. Please try again.', false);
          })
          .then(function () {
            if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = submitBtn.dataset.label || 'Submit review'; }
          });
      });
    });
  }

  function showFormMessage(form, text, ok) {
    var existing = form.querySelector('.dalen-areviews-form-msg');
    if (existing) existing.remove();
    var msg = document.createElement('p');
    msg.className = 'dalen-areviews-form-msg ' + (ok ? 'dalen-review-form__success' : 'dalen-review-form__errors');
    msg.textContent = text;
    msg.style.margin = '0 0 1rem';
    form.insertBefore(msg, form.firstChild);
    msg.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function setup(listRoot) {
    if (listRoot.dataset.areviewsInit) return;
    listRoot.dataset.areviewsInit = 'true';

    var section = listRoot.closest('.dalen-reviews-summary') || document;
    var productId = listRoot.dataset.productId || section.getAttribute('data-product-id');
    var shop = listRoot.dataset.shop || section.getAttribute('data-shop');
    if (!productId || !shop) return;

    var itemsEl = listRoot.querySelector('[data-reviews-items]');
    var sortEl = listRoot.querySelector('[data-reviews-sort]');
    var paginationEl = listRoot.querySelector('[data-reviews-pagination]');
    var pageNumsEl = listRoot.querySelector('[data-page-numbers]');
    var prevBtn = listRoot.querySelector('[data-page-prev]');
    var nextBtn = listRoot.querySelector('[data-page-next]');
    var distRows = section.querySelectorAll('.dalen-reviews-summary__col--distribution [data-reviews-filter-row]');

    // ratingFilter: 'default' = show 4 & 5 star only; '1'..'5' = that rating.
    var state = { page: 1, sort: 'date', totalPages: 1, ratingFilter: 'default', summaryRendered: false };

    function buildParams() {
      var p = new URLSearchParams();
      p.append('shop', shop);
      p.append('page', String(state.page));
      p.append('product_id', String(productId));
      p.append('customer_reviews_status', '1');
      p.append('customer_id', '');
      p.append('sort_value', state.sort);
      // 'default' (4+5) sends no server rating filter — we trim to >=4
      // client-side after parsing. A specific rating filters server-side.
      p.append('areviews_rating', state.ratingFilter === 'default' ? '' : state.ratingFilter);
      p.append('customer_country', 'country=en');
      return p;
    }

    function fetchPage() {
      if (itemsEl) itemsEl.style.opacity = '0.4';
      return fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: buildParams().toString()
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Areviews proxy ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var agg = parseAggregate(doc);
          var reviews = parseReviews(doc);
          // Default view: only 4 & 5 star reviews. (A specific star filter is
          // already applied server-side via areviews_rating.)
          if (state.ratingFilter === 'default') {
            reviews = reviews.filter(function (r) { return r.rating >= 4; });
          }
          if (!reviews.length && state.page === 1 && (!agg || !agg.total)) {
            // Nothing usable came back — keep the static fallback.
            throw new Error('Areviews returned no parseable data');
          }
          // Render the summary band (score + distribution bars) ONCE, from
          // the initial overall aggregate, then leave it fixed. Filtering by
          // a star or paginating must not re-render / animate the bars.
          if (agg && !state.summaryRendered) {
            renderSummary(section, agg);
            state.summaryRendered = true;
          }
          state.totalPages = computeTotalPages(doc, agg);
          renderCards(reviews);
          renderPagination();
          highlightFilterRows();
          if (itemsEl) itemsEl.style.opacity = '';
        })
        .catch(function (err) {
          // Leave static content untouched on failure.
          if (itemsEl) itemsEl.style.opacity = '';
          if (window.console && console.warn) console.warn('[Dalen Areviews]', err.message);
        });
    }

    /* ---------- Parsing ---------- */

    function parseAggregate(doc) {
      var avgEl = doc.querySelector('.areviews_title_rating_average') || doc.querySelector('.box_rating4_average');
      var totalEl = doc.querySelector('.AreviewsTotalNumber');
      var average = avgEl ? parseFloat((avgEl.textContent || '').replace(/[^0-9.]/g, '')) : null;
      var total = totalEl ? parseInt((totalEl.textContent || '').replace(/[^0-9]/g, ''), 10) : null;

      var dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
      var tr = extractTotalRating(doc);
      if (tr) {
        for (var k = 1; k <= 5; k++) {
          if (tr[k] && typeof tr[k].total_reviews !== 'undefined') {
            dist[k] = parseInt(tr[k].total_reviews, 10) || 0;
          }
        }
      } else {
        // Fallback: read the distribution rows in the rendered markup.
        doc.querySelectorAll('.areviews_rating_filter[data-rating]').forEach(function (row) {
          var r = parseInt(row.getAttribute('data-rating'), 10);
          var numEl = row.querySelector('.box_rating_number');
          if (r >= 1 && r <= 5 && numEl) {
            dist[r] = parseInt((numEl.textContent || '').replace(/[^0-9]/g, ''), 10) || 0;
          }
        });
      }

      if (!total) {
        total = dist[5] + dist[4] + dist[3] + dist[2] + dist[1];
      }
      if (average == null || isNaN(average)) {
        var sum = dist[5] * 5 + dist[4] * 4 + dist[3] * 3 + dist[2] * 2 + dist[1] * 1;
        average = total ? (sum / total) : 0;
      }
      return { average: average, total: total, dist: dist };
    }

    function extractTotalRating(doc) {
      var scripts = doc.querySelectorAll('script');
      for (var i = 0; i < scripts.length; i++) {
        var t = scripts[i].textContent || '';
        var m = t.match(/total_rating\s*=\s*(\{[\s\S]*?\})\s*;/);
        if (m) {
          try { return JSON.parse(m[1]); } catch (e) { /* ignore */ }
        }
      }
      return null;
    }

    function parseReviews(doc) {
      var items = doc.querySelectorAll('#list .item, .themes_container .item, .item[data-id]');
      var out = [];
      items.forEach(function (item) {
        var nameEl = item.querySelector('.areviews_user_name');
        var name = nameEl ? nameEl.textContent.trim() : '';
        var verified = !!item.querySelector('.verified_user');

        var starWrap = item.querySelector('.main_container_review_stars');
        var full = starWrap ? starWrap.querySelectorAll('.fas.fa-star').length : 0;
        var half = starWrap ? starWrap.querySelectorAll('.fa-star-half-alt').length : 0;
        var rating = Math.max(1, Math.min(5, Math.round(full + 0.5 * half)));

        // Review body — clone, strip the ellipsis + "Read More" helper bits,
        // then read the full text (visible + hidden continuation).
        var body = '';
        var commentEl = item.querySelector('.comment');
        if (commentEl) {
          var clone = commentEl.cloneNode(true);
          clone.querySelectorAll('.areviews_hide_ellipsis, .areviews_hide_text_button').forEach(function (n) { n.remove(); });
          body = (clone.textContent || '').replace(/\s+/g, ' ').trim();
        }

        var dateEl = item.querySelector('.comment_time');
        var date = dateEl ? dateEl.textContent.trim() : '';

        var image = '';
        var bigLink = item.querySelector('.image_container a.big');
        if (bigLink && bigLink.getAttribute('href')) {
          image = bigLink.getAttribute('href');
        } else {
          var imgEl = item.querySelector('.image_container img');
          if (imgEl) image = imgEl.getAttribute('data-src') || imgEl.getAttribute('src') || '';
        }
        if (image && /white_imge|loading/.test(image)) image = '';

        out.push({ name: name, verified: verified, rating: rating, body: body, date: date, image: image });
      });
      return out;
    }

    function computeTotalPages(doc, agg) {
      var max = 1;
      doc.querySelectorAll('.reviews_pagination span[role="link"], .reviews_pagination a, .reviews_pagination span').forEach(function (el) {
        var n = parseInt((el.textContent || '').trim(), 10);
        if (!isNaN(n) && n > max) max = n;
      });
      if (max <= 1 && agg && agg.total) {
        max = Math.max(1, Math.ceil(agg.total / PER_PAGE));
      }
      return max;
    }

    /* ---------- Rendering into our design ---------- */

    function renderSummary(sec, agg) {
      var avg1 = Math.round(agg.average * 10) / 10;

      var scoreEl = sec.querySelector('.dalen-reviews-summary__score');
      if (scoreEl) scoreEl.textContent = avg1;

      var countEl = sec.querySelector('.dalen-reviews-summary__count');
      if (countEl) countEl.textContent = '(' + agg.total + ' reviews)';

      var starsWrap = sec.querySelector('.dalen-reviews-summary__stars');
      if (starsWrap) {
        starsWrap.setAttribute('aria-label', avg1 + ' out of 5 stars');
        var stars = starsWrap.querySelectorAll('.dalen-reviews-summary__star');
        var avgInt = Math.floor(agg.average);
        var frac = agg.average - avgInt;
        stars.forEach(function (star, idx) {
          var i = idx + 1;
          var fill = 0;
          if (i <= avgInt) fill = 100;
          else if (i === avgInt + 1 && frac > 0) fill = Math.floor(frac * 100);
          var fillEl = star.querySelector('.dalen-reviews-summary__star-fill');
          if (fillEl) fillEl.style.width = fill + '%';
        });
      }

      // Distribution rows are 5★ → 1★ top to bottom.
      var rows = sec.querySelectorAll('.dalen-reviews-summary__col--distribution .dalen-reviews-summary__row');
      var order = [5, 4, 3, 2, 1];
      rows.forEach(function (row, idx) {
        var star = order[idx];
        if (typeof star === 'undefined') return;
        var c = agg.dist[star] || 0;
        var pct = agg.total ? (c * 100 / agg.total) : 0;
        var fill = row.querySelector('.dalen-reviews-summary__bar-fill');
        var countCell = row.querySelector('.dalen-reviews-summary__row-count');
        if (fill) fill.style.width = pct + '%';
        if (countCell) countCell.textContent = c;
      });
    }

    function renderCards(reviews) {
      if (!itemsEl) return;
      itemsEl.innerHTML = '';
      if (!reviews.length) {
        var empty = document.createElement('div');
        empty.className = 'dalen-reviews-list__empty';
        empty.innerHTML = '<p>No reviews yet — be the first to write one!</p>';
        itemsEl.appendChild(empty);
        return;
      }
      reviews.forEach(function (r) {
        itemsEl.appendChild(buildCard(r));
      });
    }

    function buildCard(r) {
      var card = document.createElement('article');
      card.className = 'dalen-review-card';
      card.setAttribute('data-review-card', '');
      card.setAttribute('data-rating', String(r.rating));

      // Author column
      var author = document.createElement('div');
      author.className = 'dalen-review-card__author';
      var nameP = document.createElement('p');
      nameP.className = 'dalen-review-card__name';
      nameP.textContent = r.name || 'Anonymous';
      author.appendChild(nameP);
      if (r.verified) {
        var v = document.createElement('span');
        v.className = 'dalen-review-card__verified';
        v.innerHTML = 'Verified Buyer <span class="dalen-review-card__verified-icon" aria-hidden="true">' +
          '<svg viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#104520"/>' +
          '<path d="M4.5 8.5l2.2 2.2 4.8-5" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
        author.appendChild(v);
      }

      // Content column
      var content = document.createElement('div');
      content.className = 'dalen-review-card__content';
      var head = document.createElement('div');
      head.className = 'dalen-review-card__content-head';
      var starsEl = document.createElement('div');
      starsEl.className = 'dalen-review-card__stars';
      starsEl.setAttribute('aria-label', r.rating + ' out of 5 stars');
      for (var i = 1; i <= 5; i++) {
        var s = document.createElement('span');
        s.className = 'dalen-review-card__star' + (i <= r.rating ? ' is-active' : '');
        s.setAttribute('aria-hidden', 'true');
        s.textContent = '★';
        starsEl.appendChild(s);
      }
      head.appendChild(starsEl);
      if (r.date) {
        var time = document.createElement('time');
        time.className = 'dalen-review-card__date';
        time.textContent = r.date;
        head.appendChild(time);
      }
      content.appendChild(head);
      if (r.body) {
        var bodyP = document.createElement('p');
        bodyP.className = 'dalen-review-card__body';
        bodyP.textContent = r.body;
        content.appendChild(bodyP);
      }

      // Image column (kept even when empty to preserve the 3-col grid)
      var imgWrap = document.createElement('div');
      imgWrap.className = 'dalen-review-card__image-wrap';
      if (r.image) {
        var img = document.createElement('img');
        img.className = 'dalen-review-card__image';
        img.src = r.image;
        img.alt = (r.name || 'Reviewer') + ' review photo';
        img.loading = 'lazy';
        img.addEventListener('error', function () { imgWrap.remove(); });
        imgWrap.appendChild(img);
      } else {
        imgWrap.style.background = 'transparent';
      }

      card.appendChild(author);
      card.appendChild(content);
      card.appendChild(imgWrap);
      return card;
    }

    function renderPagination() {
      if (!paginationEl || !pageNumsEl) return;
      paginationEl.hidden = state.totalPages <= 1;
      pageNumsEl.innerHTML = '';

      var pages = buildPageList(state.page, state.totalPages);
      pages.forEach(function (p) {
        if (p === '…') {
          var dots = document.createElement('span');
          dots.className = 'dalen-reviews-list__page-num is-ellipsis';
          dots.textContent = '…';
          pageNumsEl.appendChild(dots);
          return;
        }
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dalen-reviews-list__page-num' + (p === state.page ? ' is-active' : '');
        btn.textContent = String(p);
        btn.addEventListener('click', function () { goToPage(p); });
        pageNumsEl.appendChild(btn);
      });

      if (prevBtn) prevBtn.toggleAttribute('disabled', state.page <= 1);
      if (nextBtn) nextBtn.toggleAttribute('disabled', state.page >= state.totalPages);
    }

    /* Condensed page list: 1 … (p-1) p (p+1) … last */
    function buildPageList(current, total) {
      var pages = [];
      if (total <= 7) {
        for (var i = 1; i <= total; i++) pages.push(i);
        return pages;
      }
      pages.push(1);
      var start = Math.max(2, current - 1);
      var end = Math.min(total - 1, current + 1);
      if (start > 2) pages.push('…');
      for (var j = start; j <= end; j++) pages.push(j);
      if (end < total - 1) pages.push('…');
      pages.push(total);
      return pages;
    }

    function goToPage(p) {
      if (p < 1 || p > state.totalPages || p === state.page) return;
      state.page = p;
      fetchPage().then(scrollToTop);
    }

    function scrollToTop() {
      var top = listRoot.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }

    /* ---------- Rating filter rows ---------- */
    function highlightFilterRows() {
      distRows.forEach(function (row) {
        var r = row.getAttribute('data-rating');
        var active = state.ratingFilter === 'default'
          ? (r === '4' || r === '5')
          : (r === state.ratingFilter);
        row.classList.toggle('is-filter-active', active);
        row.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }
    function selectRating(r) {
      // Clicking the currently-active single rating clears back to default.
      if (state.ratingFilter === r) {
        state.ratingFilter = 'default';
      } else {
        state.ratingFilter = r;
      }
      state.page = 1;
      fetchPage();
    }
    distRows.forEach(function (row) {
      row.addEventListener('click', function () {
        selectRating(row.getAttribute('data-rating'));
      });
      row.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectRating(row.getAttribute('data-rating'));
        }
      });
    });

    /* ---------- Wire controls ---------- */
    if (sortEl) {
      sortEl.addEventListener('change', function () {
        state.sort = SORT_MAP[sortEl.value] || 'date';
        state.page = 1;
        fetchPage();
      });
    }
    if (prevBtn) prevBtn.addEventListener('click', function () { goToPage(state.page - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goToPage(state.page + 1); });

    // Initial load
    fetchPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
